import { useState } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Dialog } from '~/components/ui/Dialog';
import { apiUpdateProject } from '~/lib/api/projects';
import { toast } from 'react-toastify';
import type { Project } from '@shared/types/project';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: Project;
  onProjectUpdated: () => void;
}

export default function ShareDialog({ open, onOpenChange, project, onProjectUpdated }: ShareDialogProps) {
  const [toggling, setToggling] = useState(false);
  const shareUrl = `${window.location.origin}/shared/project/${project.id}`;

  async function handleTogglePublic() {
    if (toggling) {
      return;
    }

    const next = !project.isPublic;
    setToggling(true);

    try {
      await apiUpdateProject(project.id, { isPublic: next });
      onProjectUpdated();
      toast.success(next ? 'Project is now public' : 'Project is now private');
    } catch {
      toast.error("Couldn't update sharing. Please try again.");
    } finally {
      setToggling(false);
    }
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog
        showCloseButton={false}
        onBackdrop={() => onOpenChange(false)}
        onClose={() => onOpenChange(false)}
        className="!max-w-[480px] !border-0 !rounded-[14px] !bg-transparent !shadow-none"
      >
        <div className="ui-root">
          <div className="ui-modal">
            <header className="ui-modal-header">
              <span className="ui-caption min-w-0 truncate">Share {project.name}</span>
              <div className="flex-1" />
              <button type="button" className="ui-btn-icon" aria-label="Close" onClick={() => onOpenChange(false)}>
                <span className="i-ph:x w-4 h-4" />
              </button>
            </header>

            <div className="ui-modal-body">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: 'var(--color-raised)',
                  border: '1px solid var(--color-hairline)',
                  marginBottom: 18,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-primary)' }}>Public preview</div>
                  <div style={{ fontSize: 11.5, color: 'var(--color-muted)', marginTop: 2 }}>
                    Anyone with the link can view a read-only preview.
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={project.isPublic}
                  className={`ui-switch ${project.isPublic ? 'ui-switch-on' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                  onClick={handleTogglePublic}
                  disabled={toggling}
                  aria-label="Toggle public preview"
                >
                  <span className={`ui-switch-thumb ${project.isPublic ? 'ui-switch-thumb-on' : ''}`} />
                </button>
              </div>

              {project.isPublic && (
                <>
                  <label htmlFor="share-link" className="ui-label">
                    Preview link
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id="share-link"
                      type="text"
                      readOnly
                      value={shareUrl}
                      className="ui-input ui-input-mono"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      type="button"
                      className="ui-btn-secondary"
                      onClick={handleCopyLink}
                      style={{ padding: '9px 14px', flexShrink: 0 }}
                    >
                      <span className="i-ph:copy w-3.5 h-3.5" />
                      Copy
                    </button>
                  </div>
                </>
              )}
            </div>

            <footer className="ui-modal-footer flex-wrap">
              <span className="hidden sm:inline" style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                Permissions sync to deploy.
              </span>
              <div className="flex-1" />
              <button type="button" className="ui-btn-secondary" onClick={() => onOpenChange(false)}>
                Done
              </button>
            </footer>
          </div>
        </div>
      </Dialog>
    </RadixDialog.Root>
  );
}
